import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { useSelector } from 'react-redux';

import DiscardChanges from '@/src/components/common/DiscardChanges/DiscardChanges';
import SectionLoader from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import NavigationBlocker from '@/src/components/common/NavigationBlocker/NavigationBlocker';
import { showToast } from '@/src/globalFunction';
import { RootState } from '@/src/reducers/types';
import { cn } from '@/src/util/ui';

import { useGetScope } from '../api/getScope';
import { useUpdateScope } from '../api/updateScope';

type FormValues = {
  transactionsVolume: string;
  estimatedTimeAllocated: string;
};

export const ScopeTab = () => {
  const project = useSelector((store: RootState) => store.project?.Selected);
  const loggedUser = useSelector((store: RootState) => store.loggedUser);

  const [layout, setLayout] = useState<'view' | 'form'>('view');
  const projectId = project.id;

  const scopeQuery = useGetScope({
    projectId,
    config: { enabled: !!projectId },
  });

  const updateScopeMutation = useUpdateScope({ projectId });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      transactionsVolume: '',
      estimatedTimeAllocated: '',
    },
    mode: 'onChange',
  });

  const values = watch();

  const withPermission =
    loggedUser.data.id === project.createdBy ||
    project.leadId === loggedUser.data.id ||
    loggedUser.data.userRole < 4;

  const hasChanges = useMemo(() => {
    if (!scopeQuery.data) return false;

    const apiVolume = scopeQuery.data?.['transactions-volume'] ?? '';
    const apiEstimate = scopeQuery.data?.['estimated-time-allocation-hrs'] ?? '';

    return (
      String(values.transactionsVolume ?? '') !== String(apiVolume) ||
      String(values.estimatedTimeAllocated ?? '') !== String(apiEstimate)
    );
  }, [values, scopeQuery.data]);

  const handleChangeView = (e: React.MouseEvent, view: 'view' | 'form') => {
    e.preventDefault();
    setLayout(view);
  };

  const handleCancel = () => {
    if (hasChanges) {
      $('#companyProfileScope').modal('show');
    } else {
      resetForm();
      setLayout('view');
    }
  };

  const onSubmit = async (data: FormValues) => {
    const payload = {
      projectId,
      transactionsVolume: Number(data.transactionsVolume) || 0,
      estimatedTimeAllocated: Number(data.estimatedTimeAllocated) || 0,
    };

    await updateScopeMutation.mutateAsync(payload);
    setLayout('view');
    showToast('success', 'Changes successfully saved!');
  };

  const handleDiscard = () => {
    resetForm();
    setLayout('view');
    $('#companyProfileScope').modal('hide');
    showToast('success', 'Changes have been discarded');
  };

  const resetForm = useCallback(() => {
    if (!scopeQuery.data) {
      reset({ transactionsVolume: '', estimatedTimeAllocated: '' });
      return;
    }

    reset({
      transactionsVolume: `${scopeQuery.data['transactions-volume'] ?? ''}`,
      estimatedTimeAllocated: `${scopeQuery.data['estimated-time-allocation-hrs'] ?? ''}`,
    });
  }, [reset, scopeQuery.data]);

  useEffect(() => {
    resetForm();
  }, [scopeQuery.data, resetForm]);

  const isNavigationBlocked = hasChanges && layout === 'form';
  const isLoading = scopeQuery.isLoading;

  return (
    <div className="memberFormContainer mt0 tw-relative">
      {isLoading && <SectionLoader />}

      {withPermission && (
        <div className="formsHeaderActions tw-absolute tw-right-[5px] tw-top-[-60px]">
          {layout === 'view' ? (
            <a
              href="/"
              className="editProfile tw-flex tw-items-center tw-text-cfo-blue-600"
              onClick={(e) => handleChangeView(e, 'form')}
            >
              <i className="material-icons tw-text-[20px] tw-mr-[7px]">edit</i>
              Edit
            </a>
          ) : (
            <div className="tw-flex">
              <button
                type="button"
                onClick={handleCancel}
                className={cn(
                  'tw-bg-cfo-grey-100 tw-border tw-border-gray-200 tw-rounded-md tw-font-medium tw-px-[10px] tw-py-[6px]',
                  'tw-text-cfo-black hover:tw-bg-[#EAF0FF]'
                )}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit(onSubmit)}
                disabled={!hasChanges}
                className={cn(
                  'tw-rounded-md tw-font-medium tw-px-[10px] tw-py-[6px] tw-ml-[10px]',
                  hasChanges
                    ? 'tw-bg-cfo-blue-700 tw-text-white'
                    : 'tw-bg-cfo-grey-100 tw-text-gray-400 tw-pointer-events-none'
                )}
              >
                Save
              </button>
            </div>
          )}
        </div>
      )}

      <div className="memberFormBody">
        <form className="pt20 tw-space-y-6">
          <div className="tw-flex">
            <label htmlFor="transactionsVolume" className="tw-w-[260px] tw-font-brandonTextMedium">
              Transaction Volume
            </label>
            {layout === 'view' ? (
              <span>{values.transactionsVolume || '-'}</span>
            ) : (
              <div className="tw-flex tw-items-center tw-gap-1">
                <Controller
                  name="transactionsVolume"
                  control={control}
                  rules={{
                    validate: {
                      positive: (v) => Number(v) > -1 || 'Please enter value greater than -1.',
                    },
                  }}
                  render={({ field }) => (
                    <NumericFormat
                      {...field}
                      allowNegative={false}
                      decimalScale={0}
                      allowLeadingZeros={false}
                      valueIsNumericString
                      className={cn(
                        'tw-border tw-rounded tw-px-2 tw-py-1 tw-font-brandonTextMedium',
                        errors.transactionsVolume
                          ? '!tw-border-red-500 !tw-text-cfo-red-400 focus:!tw-ring-red-500'
                          : ''
                      )}
                    />
                  )}
                />
                {errors.transactionsVolume && (
                  <span className="tw-text-cfo-red-400 tw-text-[14px] tw-font-[390] tw-ml-2">
                    {errors.transactionsVolume.message}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="tw-flex">
            <label
              htmlFor="estimatedTimeAllocated"
              className="tw-w-[260px] tw-font-brandonTextMedium"
            >
              Estimated Time Allocated (in hrs)
            </label>
            {layout === 'view' ? (
              <span>{values.estimatedTimeAllocated || '-'}</span>
            ) : (
              <div className="tw-flex tw-items-center tw-gap-1">
                <Controller
                  name="estimatedTimeAllocated"
                  control={control}
                  rules={{
                    validate: {
                      positive: (v) => Number(v) > -1 || 'Please enter value greater than -1.',
                      decimals: (v) =>
                        !v.includes('.') ||
                        v.split('.')[1].length <= 2 ||
                        'Please enter up to 2 decimal places only.',
                    },
                  }}
                  render={({ field }) => (
                    <NumericFormat
                      {...field}
                      allowNegative={false}
                      decimalScale={2}
                      fixedDecimalScale={false}
                      valueIsNumericString
                      className={cn(
                        'tw-border tw-rounded tw-px-2 tw-py-1 tw-font-brandonTextMedium',
                        errors.estimatedTimeAllocated
                          ? '!tw-border-red-500 !tw-text-cfo-red-400 focus:!tw-ring-red-500'
                          : ''
                      )}
                    />
                  )}
                />
                {errors.estimatedTimeAllocated && (
                  <span className="tw-text-cfo-red-400 tw-text-[14px] tw-font-[390] tw-ml-2">
                    {errors.estimatedTimeAllocated.message}
                  </span>
                )}
              </div>
            )}
          </div>
        </form>
      </div>

      <DiscardChanges onCancel={handleDiscard} id="companyProfileScope" />
      <NavigationBlocker navigationBlocked={isNavigationBlocked} />
    </div>
  );
};
